// Client-callable server functions for the notification system.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { NOTIFICATION_TYPES, type NotificationType } from "./notifications-shared";

const TYPE_SCHEMA = z.enum(NOTIFICATION_TYPES);

// Load all notification preferences for the current user.
// Auto-seeds defaults on first read.
export const getNotificationPrefs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("notification_preferences")
      .select("notification_type, enabled, custom_time")
      .eq("user_id", userId);

    const have = new Set((existing ?? []).map((r) => r.notification_type));
    const missing = NOTIFICATION_TYPES.filter((t) => !have.has(t));
    if (missing.length > 0) {
      await supabase
        .from("notification_preferences")
        .upsert(
          missing.map((t) => ({
            user_id: userId,
            notification_type: t,
            enabled: true,
          })),
          { onConflict: "user_id,notification_type" },
        );
    }

    const { data: prefs } = await supabase
      .from("notification_preferences")
      .select("notification_type, enabled, custom_time")
      .eq("user_id", userId);

    const byType: Record<string, { enabled: boolean; customTime: string | null }> = {};
    for (const t of NOTIFICATION_TYPES) {
      byType[t] = { enabled: true, customTime: null };
    }
    for (const row of prefs ?? []) {
      byType[row.notification_type] = {
        enabled: row.enabled,
        customTime: row.custom_time,
      };
    }

    // Also detect whether the user has an active push subscription.
    const { data: pushRows } = await supabase
      .from("push_subscriptions")
      .select("id")
      .eq("user_id", userId)
      .eq("is_active", true)
      .limit(1);

    return { prefs: byType, hasActivePush: (pushRows?.length ?? 0) > 0 };
  });

export const setNotificationPref = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        type: TYPE_SCHEMA,
        enabled: z.boolean().optional(),
        customTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const patch: {
      user_id: string;
      notification_type: NotificationType;
      enabled?: boolean;
      custom_time?: string | null;
    } = {
      user_id: userId,
      notification_type: data.type,
    };
    if (data.enabled !== undefined) patch.enabled = data.enabled;
    if (data.customTime !== undefined) patch.custom_time = data.customTime;
    const { error } = await supabase
      .from("notification_preferences")
      .upsert([patch], { onConflict: "user_id,notification_type" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const captureTimezone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ timezone: z.string().min(1).max(80) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("profiles")
      .update({ user_timezone: data.timezone })
      .eq("id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ------------------ Push subscription management ------------------

const PUSH_SUB_SCHEMA = z.object({
  endpoint: z.string().url().max(2048),
  p256dh: z.string().min(1).max(512),
  auth: z.string().min(1).max(512),
  userAgent: z.string().max(512).optional(),
});

export const pushSubscribe = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => PUSH_SUB_SCHEMA.parse(data))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    // Random per-subscription secret. Returned to the client once and used
    // by the service worker on pushsubscriptionchange to prove ownership of
    // the old endpoint when requesting resubscription.
    const resubscribeToken = crypto.randomUUID() + crypto.randomUUID().replace(/-/g, "");
    const { data: row, error } = await supabase
      .from("push_subscriptions")
      .upsert(
        {
          user_id: userId,
          endpoint: data.endpoint,
          p256dh: data.p256dh,
          auth: data.auth,
          user_agent: data.userAgent ?? null,
          is_active: true,
          last_used_at: new Date().toISOString(),
          resubscribe_token: resubscribeToken,
        },
        { onConflict: "endpoint" },
      )
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    if (!row?.id) throw new Error("push_subscriptions upsert returned no row");
    return { ok: true, id: row.id as string, resubscribeToken };
  });

export const pushUnsubscribe = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ endpoint: z.string().url().optional() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    let q = supabase
      .from("push_subscriptions")
      .update({ is_active: false })
      .eq("user_id", userId);
    if (data.endpoint) q = q.eq("endpoint", data.endpoint);
    const { error } = await q;
    if (error) throw new Error(error.message);
    return { ok: true };
  });
